import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminTable from "../../Common/AdminTable";
import AdminModal from "../../Common/AdminModal";
import ConfirmDialog from "../../Common/ConfirmDialog";
import {
  getTestsList,
  updateTest,
  toggleTestPublish,
  toggleTestActive,
} from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { ROUTES } from "../../../../constants/routes";
import {
  Plus,
  Edit2,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Layers,
  CheckCircle2,
  Settings2,
} from "lucide-react";

export default function TestManagementPage() {
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [planFilter, setPlanFilter] = useState("All");
  const [search, setSearch] = useState("");

  const [configuringTest, setConfiguringTest] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [toastMessage, setToastMessage] = useState("");

  const loadTests = async () => {
    setLoading(true);
    try {
      const data = await getTestsList({
        status: statusFilter,
        plan: planFilter,
        search,
      });
      setTests(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTests();
  }, [statusFilter, planFilter]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  const handleTogglePublish = async (test) => {
    const shouldPublish = test.status !== "Published";
    setConfirmDialog({
      isOpen: true,
      title: shouldPublish ? "Publish Test" : "Unpublish Test",
      message: shouldPublish
        ? `Are you sure you want to publish "${test.title}"? It will become available to eligible candidates.`
        : `Are you sure you want to unpublish "${test.title}"? Candidates will no longer be able to start new attempts.`,
      confirmText: shouldPublish ? "Publish" : "Unpublish",
      confirmVariant: shouldPublish ? "primary" : "danger",
      onConfirm: async () => {
        try {
          await toggleTestPublish(test.id, shouldPublish, adminUser.email);
          showToast(`Test ${test.id} ${shouldPublish ? "Published" : "Unpublished"}`);
          await loadTests();
        } catch (err) {
          alert("Action failed: " + err.message);
        }
      },
    });
  };

  const handleToggleActive = async (test) => {
    const nextState = !test.is_active;
    try {
      await toggleTestActive(test.id, nextState, adminUser.email);
      showToast(`Test ${test.id} ${nextState ? "Activated" : "Deactivated"}`);
      await loadTests();
    } catch (err) {
      alert("Action failed: " + err.message);
    }
  };

  const handleSaveConfig = async () => {
    if (!configuringTest) return;
    try {
      await updateTest(
        configuringTest.id,
        {
          availability_start: configuringTest.availability_start,
          availability_end: configuringTest.availability_end,
          attempt_limit: parseInt(configuringTest.attempt_limit) || 1,
          plan_tier: configuringTest.plan_tier || "All",
        },
        adminUser.email
      );
      showToast(`Updated availability & plan access for ${configuringTest.id}`);
      setConfiguringTest(null);
      await loadTests();
    } catch (err) {
      alert("Failed to update config: " + err.message);
    }
  };

  const columns = [
    {
      title: "Test ID / Code",
      key: "id",
      width: "140px",
      render: (val, row) => (
        <div>
          <span style={{ fontWeight: 700, color: "#0284c7" }}>{val}</span>
          <div style={{ fontSize: "11px", color: "#64748b" }}>{row.code}</div>
        </div>
      ),
    },
    {
      title: "Test Title",
      key: "title",
      render: (val, row) => {
        const totalQ = (row.sections || []).reduce(
          (acc, s) => acc + (s.question_ids || []).length,
          0
        );
        return (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              {row.duration_minutes} Mins &bull; {row.total_marks} Marks &bull; {totalQ} Questions &bull; {row.sections?.length || 1} Sections
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: "110px",
      render: (val) => {
        const cls = (val || "").toLowerCase();
        return <span className={`status-badge ${cls}`}>{val}</span>;
      },
    },
    {
      title: "Plan Entitlement",
      key: "plan_tier",
      width: "130px",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "4px",
            background: val === "Premium" ? "#fdf4ff" : "#f1f5f9",
            color: val === "Premium" ? "#9333ea" : "#475569",
            border: `1px solid ${val === "Premium" ? "#f0abfc" : "#cbd5e1"}`,
          }}
        >
          {val || "All"} Tier
        </span>
      ),
    },
    {
      title: "Active",
      key: "is_active",
      width: "90px",
      render: (val, row) => (
        <label
          style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", cursor: "pointer" }}
          title={val ? "Deactivate test" : "Activate test"}
        >
          <input
            type="checkbox"
            checked={Boolean(val)}
            onChange={() => handleToggleActive(row)}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span
            style={{
              position: "absolute",
              cursor: "pointer",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: val ? "#16a34a" : "#cbd5e1",
              transition: ".2s",
              borderRadius: "20px",
            }}
          >
            <span
              style={{
                position: "absolute",
                content: '""',
                height: "14px",
                width: "14px",
                left: val ? "19px" : "3px",
                bottom: "3px",
                backgroundColor: "white",
                transition: ".2s",
                borderRadius: "50%",
              }}
            />
          </span>
        </label>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "160px",
      render: (_, row) => (
        <div style={{ display: "flex", gap: "6px" }}>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => navigate(`${ROUTES.ADMIN_TESTS_BUILDER}?testId=${row.id}`)}
            title="Edit in Test Builder"
          >
            <Edit2 size={13} />
          </button>
          <button
            className="admin-btn admin-btn-secondary admin-btn-sm"
            onClick={() => setConfiguringTest({ ...row })}
            title="Configure dates, attempts & plan"
          >
            <Settings2 size={13} />
          </button>
          <button
            className={`admin-btn admin-btn-sm ${
              row.status === "Published" ? "admin-btn-secondary" : "admin-btn-primary"
            }`}
            onClick={() => handleTogglePublish(row)}
            title={row.status === "Published" ? "Unpublish Test" : "Publish Test"}
          >
            {row.status === "Published" ? "Unpublish" : "Publish"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      {/* Top Filter Bar */}
      <div className="admin-card" style={{ marginBottom: "16px", padding: "14px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Status:</span>
              {["All", "Published", "Draft"].map((st) => (
                <button
                  key={st}
                  className={`admin-btn admin-btn-sm ${
                    statusFilter === st ? "admin-btn-primary" : "admin-btn-secondary"
                  }`}
                  onClick={() => setStatusFilter(st)}
                >
                  {st}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Plan:</span>
              <select
                className="admin-form-select"
                style={{ width: "130px", padding: "4px 8px", fontSize: "13px" }}
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
              >
                <option value="All">All Plans</option>
                <option value="Free">Free Tier</option>
                <option value="Premium">Premium</option>
              </select>
            </div>
          </div>

          <button
            className="admin-btn admin-btn-primary"
            onClick={() => navigate(ROUTES.ADMIN_TESTS_BUILDER)}
          >
            <Plus size={15} /> Create New Test
          </button>
        </div>
      </div>

      {/* Tests Listing Table */}
      <AdminTable
        columns={columns}
        data={tests}
        searchPlaceholder="Search tests by title, code, description..."
        searchQuery={search}
        onSearchChange={(val) => {
          setSearch(val);
          loadTests();
        }}
        pageSize={10}
        rowKey="id"
      />

      {/* Configure Dates & Entitlements Modal */}
      <AdminModal
        isOpen={Boolean(configuringTest)}
        onClose={() => setConfiguringTest(null)}
        title={configuringTest ? `Configure Test: ${configuringTest.id}` : ""}
        maxWidth="540px"
        footer={
          <>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => setConfiguringTest(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-primary"
              onClick={handleSaveConfig}
            >
              Save Configuration
            </button>
          </>
        }
      >
        {configuringTest && (
          <div>
            <div className="admin-form-group">
              <label className="admin-form-label">Plan / Entitlement Tier Assignment</label>
              <select
                className="admin-form-select"
                value={configuringTest.plan_tier || "All"}
                onChange={(e) =>
                  setConfiguringTest({ ...configuringTest, plan_tier: e.target.value })
                }
              >
                <option value="All">All Users (Open / Free + Paid)</option>
                <option value="Free">Free Plan Users Only</option>
                <option value="Premium">Premium Plan Users Only</option>
              </select>
              <span style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", display: "block" }}>
                Integrates with the existing User &rarr; Plan &rarr; Access model.
              </span>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Candidate Attempt Limit</label>
              <input
                type="number"
                min="1"
                className="admin-form-input"
                value={configuringTest.attempt_limit || 1}
                onChange={(e) =>
                  setConfiguringTest({ ...configuringTest, attempt_limit: e.target.value })
                }
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Available From</label>
              <input
                type="datetime-local"
                className="admin-form-input"
                value={configuringTest.availability_start ? configuringTest.availability_start.substring(0, 16) : ""}
                onChange={(e) =>
                  setConfiguringTest({ ...configuringTest, availability_start: e.target.value })
                }
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label">Available Until</label>
              <input
                type="datetime-local"
                className="admin-form-input"
                value={configuringTest.availability_end ? configuringTest.availability_end.substring(0, 16) : ""}
                onChange={(e) =>
                  setConfiguringTest({ ...configuringTest, availability_end: e.target.value })
                }
              />
            </div>
          </div>
        )}
      </AdminModal>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
      />
    </div>
  );
}
